import { EQuizCorrectionMode, EQuizMode } from '@constants';
import { ETrackingEventType } from '@constants';
import { IStudentQuestion, IStudentQuiz } from '@sdk/contracts';
import Cookies from 'js-cookie';
import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

import { useCookies, useCountdown } from '.';

import { useTrackingEvents } from '@/hooks/useTrackingEvents';
import { useMutateQuizResults } from '@/store';

const QUIZ_SHORTCUT_COOKIE = 'quiz_shortcut_cookie';
const QUIZ_CORRECTION_COOKIE = 'quiz_correction_cookie';

type CorrectCountUpdater = (prev: number) => number;
const isCorrectCountUpdater = (value: number | CorrectCountUpdater): value is CorrectCountUpdater =>
  typeof value === 'function';

interface IStore {
  studentQuiz?: IStudentQuiz;
  questionIndex: number;
  currentStudentQuestion?: IStudentQuestion;
  responses: Record<string, number | null>;
  correctQuestionsInstantly: boolean;
  selectedAnswer?: number;
  correctCount: number;
  correctionMode: EQuizCorrectionMode;
  quizFromActivityPath: string;
  loadingScreen: boolean;
  setLoadingScreen: (loadingScreen: boolean) => void;
  setCorrectCount: (correctCount: number | CorrectCountUpdater) => void;
  setStudentQuiz: (studentQuiz?: IStudentQuiz) => void;
  setCurrentStudentQuestion: (currentStudentQuestion?: IStudentQuestion) => void;
  setQuestionIndex: (questionIndex: number) => void;
  prevQuestion: () => void;
  nextQuestion: () => void;
  setResponses: (responses: Record<string, number | null>) => void;
  updateResponse: (questionId: string, answerValue: number | null) => void;
  resetQuizInfo: () => void;
  setCorrectQuestionsInstantly: (correctQuestionsInstantly: boolean) => void;
  setSelectedAnswer: (selectedAnswe?: number) => void;
  setCorrectionMode: (correctionMode: EQuizCorrectionMode) => void;
  addQuestionToQuiz: (question: IStudentQuestion) => void;
  setQuizFromActivityPath: (quizFromActivityPath: string) => void;
}

export const quizStore = create<IStore>(set => ({
  questionIndex: 0,
  responses: {},
  correctQuestionsInstantly: false,
  correctCount: 0,
  quizFromActivityPath: '',
  loadingScreen: true,
  setLoadingScreen: (loadingScreen: boolean) =>
    set(state => (state.loadingScreen === loadingScreen ? state : { loadingScreen })),
  setCorrectCount: correctCount =>
    set(state => {
      const nextValue = isCorrectCountUpdater(correctCount)
        ? correctCount(state.correctCount)
        : correctCount;
      return state.correctCount === nextValue ? state : { correctCount: nextValue };
    }),
  setQuizFromActivityPath: quizFromActivityPath => set({ quizFromActivityPath }),
  setQuestionIndex: questionIndex =>
    set(state => (state.questionIndex === questionIndex ? state : { questionIndex })),
  prevQuestion: () => set(state => ({ questionIndex: state.questionIndex - 1 })),
  nextQuestion: () => set(state => ({ questionIndex: state.questionIndex + 1 })),
  setResponses: responses => set(state => (state.responses === responses ? state : { responses })),
  updateResponse: (questionId, answerValue) =>
    set(state => ({ responses: { ...state.responses, [questionId]: answerValue } })),
  resetQuizInfo: () => set({ questionIndex: 0, responses: {}, loadingScreen: true }),
  setCorrectQuestionsInstantly: correctQuestionsInstantly =>
    set(state =>
      state.correctQuestionsInstantly === correctQuestionsInstantly
        ? state
        : { correctQuestionsInstantly }
    ),
  setSelectedAnswer: selectedAnswer =>
    set(state => (state.selectedAnswer === selectedAnswer ? state : { selectedAnswer })),
  setStudentQuiz: studentQuiz =>
    set(state => (state.studentQuiz === studentQuiz ? state : { studentQuiz })),
  setCurrentStudentQuestion: currentStudentQuestion =>
    set(state =>
      state.currentStudentQuestion?.question._id === currentStudentQuestion?.question._id
        ? state
        : { currentStudentQuestion }
    ),
  correctionMode:
    (Cookies.get(QUIZ_CORRECTION_COOKIE) as EQuizCorrectionMode) ||
    EQuizCorrectionMode.CORRECT_INSTANTLY,
  setCorrectionMode: correctionMode => {
    set({ correctionMode });
    Cookies.set(QUIZ_CORRECTION_COOKIE, correctionMode, {
      domain: import.meta.env.VITE_COOKIE_DOMAIN,
      path: '/',
      expires: 365
    });
  },
  addQuestionToQuiz: question =>
    set(state => {
      if (!state.studentQuiz) return state;
      return {
        studentQuiz: {
          ...state.studentQuiz,
          studentQuestions: [...state.studentQuiz.studentQuestions, question]
        }
      };
    })
}));

export const useResponseForQuestion = (questionId: string) =>
  quizStore(state => state.responses[questionId]);

export const useIsCorrectInstantlyActive = () =>
  quizStore(state => state.correctionMode === EQuizCorrectionMode.CORRECT_INSTANTLY);

export const useQuizSetQuestionIndex = () => quizStore(state => state.setQuestionIndex);
export const useQuizUpdateResponse = () => quizStore(state => state.updateResponse);

export const useQuizInfo = () => {
  const {
    responses,
    studentQuiz,
    correctCount,
    questionIndex,
    selectedAnswer,
    correctionMode,
    currentStudentQuestion,
    correctQuestionsInstantly,
    loadingScreen,
    nextQuestion,
    prevQuestion,
    setResponses,
    resetQuizInfo,
    setStudentQuiz,
    setCorrectCount,
    setQuestionIndex,
    setCorrectionMode,
    setSelectedAnswer,
    setCurrentStudentQuestion,
    setCorrectQuestionsInstantly,
    addQuestionToQuiz,
    setQuizFromActivityPath,
    setLoadingScreen,
    quizFromActivityPath
  } = quizStore();
  const { calculateQuizResults, isWorking: isMutationWorking } = useMutateQuizResults();
  const { trackEvent } = useTrackingEvents();
  const navigateTo = useNavigate();
  const { spentTime, reset } = useCountdown();

  const [hasClosedQuizShortcut, setHasClosedQuizShortcut] = useCookies(
    QUIZ_SHORTCUT_COOKIE,
    Cookies.get(QUIZ_SHORTCUT_COOKIE) || 'false'
  );
  const [isShortcutOpen, setIsShortcutOpen] = useState(!hasClosedQuizShortcut);

  useEffect(() => {
    if (studentQuiz) {
      setCurrentStudentQuestion(studentQuiz.studentQuestions[0]);
      if (studentQuiz.mode === EQuizMode.LIGHTNING) {
        setCorrectQuestionsInstantly(true);
      }
    }
  }, [studentQuiz]);

  useEffect(() => {
    if (studentQuiz) {
      setCurrentStudentQuestion(studentQuiz.studentQuestions[questionIndex]);
    }
  }, [questionIndex, studentQuiz]);

  const toggleShortcut = () => {
    setIsShortcutOpen(prevState => !prevState);
    setHasClosedQuizShortcut(isShortcutOpen);
  };

  const toggleCorrectionMode = () => {
    setCorrectionMode(
      correctionMode === EQuizCorrectionMode.CORRECT_INSTANTLY
        ? EQuizCorrectionMode.CORRECT_ON_REVIEW
        : EQuizCorrectionMode.CORRECT_INSTANTLY
    );
  };

  const handleNextQuestion = () => {
    if (
      currentStudentQuestion?.question._id &&
      (correctionMode !== EQuizCorrectionMode.CORRECT_INSTANTLY ||
        responses[currentStudentQuestion.question._id] === undefined)
    ) {
      if (selectedAnswer && currentStudentQuestion.question.correctAnswer === selectedAnswer) {
        setCorrectCount(correctCount + 1);
      }
      handleAnswerChange(currentStudentQuestion.question._id, selectedAnswer ?? null);
      if (
        correctionMode !== EQuizCorrectionMode.CORRECT_INSTANTLY &&
        questionIndex !== (studentQuiz?.studentQuestions.length ?? 0) - 1
      )
        nextQuestion();
      return;
    }
    nextQuestion();
  };

  const handleAnswerChange = useCallback(
    (questionId: string, answerValue: number | null) => {
      const updatedResponses = {
        ...responses,
        [questionId]: answerValue
      };
      setResponses(updatedResponses);
    },
    [responses]
  );

  const handleSendResponses = () => {
    if (!studentQuiz) return;
    const quizResponses = Object.entries(responses).map(([question, givenAnswer]) => ({
      question,
      givenAnswer: isNil(givenAnswer) ? undefined : Number(givenAnswer)
    }));
    calculateQuizResults(
      {
        responses: quizResponses,
        studentQuiz: studentQuiz._id,
        spentTime
      },
      {
        onSuccess: quizResult => {
          trackEvent({
            eventType: ETrackingEventType.QUIZ_COMPLETED,
            eventData: { studentQuiz: studentQuiz._id }
          });

          reset();
          setCorrectCount(0);
          if (quizFromActivityPath.length > 0) {
            setQuizFromActivityPath('');
            navigateTo(`${quizFromActivityPath}`);
          } else {
            navigateTo(`/tests/results/${quizResult._id}?openModal=true`);
          }
        }
      }
    );
  };

  return {
    questionIndex,
    responses,
    setQuestionIndex,
    prevQuestion,
    nextQuestion,
    setResponses,
    resetQuizInfo,
    correctQuestionsInstantly,
    setCorrectQuestionsInstantly,
    isShortcutOpen,
    toggleShortcut,
    selectedAnswer,
    setSelectedAnswer,
    studentQuiz,
    setStudentQuiz,
    currentStudentQuestion,
    setCurrentStudentQuestion,
    handleNextQuestion,
    toggleCorrectionMode,
    isCorrectInstantlyActive: correctionMode === EQuizCorrectionMode.CORRECT_INSTANTLY,
    correctCount,
    setCorrectCount,
    handleAnswerChange,
    addQuestionToQuiz,
    handleSendResponses,
    setQuizFromActivityPath,
    loadingScreen,
    setLoadingScreen,
    isWorking: isMutationWorking
  };
};
